import React, { createContext, useContext, useEffect, useReducer } from "react";

const CartContext = createContext();

const initialState = {
  items: [],
};

const getProductKey = (product) => product?.id ?? product?.uuid ?? product?.product_id ?? null;

const normalizeCartItem = (item) => {
  if (!item) return null;

  const product = item.product || item;
  const key = getProductKey(product);

  if (!product || key == null) return null;

  return {
    product: {
      ...product,
      id: product.id ?? product.uuid ?? key,
      uuid: product.uuid ?? product.id ?? key,
    },
    quantity: Math.max(1, Number(item.quantity || 1)),
  };
};

const normalizeCartState = (state) => {
  const items = Array.isArray(state?.items) ? state.items : [];
  return {
    items: items.map(normalizeCartItem).filter(Boolean),
  };
};

function cartReducer(state, action) {
  switch (action.type) {
    case "ADD_ITEM": {
      const normalized = normalizeCartItem({
        product: action.product,
        quantity: action.quantity,
      });

      if (!normalized) return state;

      const key = getProductKey(normalized.product);
      const existing = state.items.find((item) => getProductKey(item.product) === key);

      if (existing) {
        return {
          ...state,
          items: state.items.map((item) =>
            getProductKey(item.product) === key
              ? { ...item, quantity: item.quantity + normalized.quantity }
              : item
          ),
        };
      }

      return {
        ...state,
        items: [...state.items, normalized],
      };
    }

    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((item) => getProductKey(item.product) !== action.id),
      };

    case "UPDATE_QUANTITY":
      return {
        ...state,
        items: state.items.map((item) =>
          getProductKey(item.product) === action.id
            ? { ...item, quantity: Math.max(1, Number(action.quantity || 1)) }
            : item
        ),
      };

    case "CLEAR_CART":
      return initialState;

    case "SET_CART":
      return normalizeCartState(action.state);

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(normalizeCartState(state)));
  }, [state]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("cart");
      if (!stored) return;
      dispatch({ type: "SET_CART", state: JSON.parse(stored) });
    } catch (error) {
      console.error("Failed to restore cart:", error);
      localStorage.removeItem("cart");
    }
  }, []);

  const addItem = (product, quantity = 1) => {
    dispatch({ type: "ADD_ITEM", product, quantity });
  };

  const removeItem = (id) => dispatch({ type: "REMOVE_ITEM", id });
  const updateQuantity = (id, quantity) => dispatch({ type: "UPDATE_QUANTITY", id, quantity });
  const clearCart = () => dispatch({ type: "CLEAR_CART" });

  return (
    <CartContext.Provider value={{ ...state, addItem, removeItem, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
